import React, { Fragment, useCallback, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import type { StudentPayload } from './checkInTypes';

type StudentDeleteConfirmDialogProps = {
  open: boolean;
  student: StudentPayload | null;
  onClose: () => void;
  onDeleted: (studentId: string) => void;
};

const StudentDeleteConfirmDialog = ({
  open,
  student,
  onClose,
  onDeleted,
}: StudentDeleteConfirmDialogProps) => {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDelete = useCallback(async () => {
    if (!student) return;
    setDeleting(true);
    setError(null);
    try {
      const res = await fetch(`/api/students/${student.id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) {
        const data = (await res.json().catch(() => null)) as { error?: string } | null;
        throw new Error(data?.error ?? `Delete failed (${res.status})`);
      }
      onDeleted(student.id);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Delete failed');
    } finally {
      setDeleting(false);
    }
  }, [onClose, onDeleted, student]);

  const displayName = student
    ? [student.firstName, student.lastName].filter(Boolean).join(' ')
    : 'this student';

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog as="div" className="relative z-[60]" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-ink/30" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md rounded-3xl border-[3px] border-ink/15 bg-white p-6 shadow-[4px_8px_0_rgba(44,44,44,0.1)]">
                <Dialog.Title className="font-display text-xl font-semibold text-ink">
                  Delete student?
                </Dialog.Title>
                <Dialog.Description className="mt-2 font-sans text-sm text-ink/70">
                  This will permanently remove <span className="font-semibold text-ink">{displayName}</span>.
                  This cannot be undone.
                </Dialog.Description>

                {error ? (
                  <p className="mt-4 rounded-xl bg-doghouse/15 px-3 py-2 text-sm font-semibold text-ink" role="alert">
                    {error}
                  </p>
                ) : null}

                <div className="mt-6 flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={onClose}
                    disabled={deleting}
                    className="rounded-full px-4 py-2 text-sm font-semibold text-ink/70 hover:bg-ink/5 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => void handleDelete()}
                    disabled={deleting}
                    className="rounded-full bg-doghouse/80 px-5 py-2 text-sm font-semibold text-white ring-2 ring-ink/10 hover:bg-doghouse disabled:opacity-50"
                  >
                    {deleting ? 'Deleting…' : 'Delete'}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default StudentDeleteConfirmDialog;
